import { NexusRoutes } from '../routes';
import { BaseAPIClient } from './BaseAPIClient';
import {
  FetchAccountHoldingsRequest,
  FetchAccountHoldingsResponse,
  FetchAccountRequest,
  FetchAccountResponse,
  FetchAccountsRequest,
  FetchAccountsResponse,
  FetchAccountDocumentsRequest,
  FetchAccountDocumentsResponse,
  FetchAccountTransactionsRequest,
  FetchAccountTransactionsResponse,
  FetchWalletBalanceRequest,
  FetchWalletBalanceResponse,
  UpdateBankAccountRequest,
  UpdateBankAccountResponse,
  FetchBankAccountRequest,
  FetchBankAccountResponse,
} from './APIClientInterface';

export class APIClient extends BaseAPIClient {
  static async fetchAccounts(request: FetchAccountsRequest): Promise<FetchAccountsResponse> {
    const { client, config } = this.createClient();
    const { page_size, page } = request;

    return client
      .get(`${NexusRoutes.Accounts}?page_size=${page_size}&page=${page}`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountsResponse>(request, httpResponse);
        if (response.success) {
          response.accounts = httpResponse.data.results;
          response.nextPageUrl = httpResponse.data.next;
        }

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountsResponse>(request, error));
  }

  static async fetchAccount(request: FetchAccountRequest): Promise<FetchAccountResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.Account(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountResponse>(request, httpResponse);
        if (response.success) response.account = httpResponse.data;

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountResponse>(request, error));
  }

  static async fetchAccountHoldings(request: FetchAccountHoldingsRequest): Promise<FetchAccountHoldingsResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.AccountHoldings(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountHoldingsResponse>(request, httpResponse);
        if (response.success) response.accountHoldings = httpResponse.data;

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountHoldingsResponse>(request, error));
  }

  static async fetchWalletBalance(request: FetchWalletBalanceRequest): Promise<FetchWalletBalanceResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.WalletBalance(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchWalletBalanceResponse>(request, httpResponse);
        if (response.success) response.walletBalance = httpResponse.data;

        return response;
      })
      .catch(error => this.prepareResponse<FetchWalletBalanceResponse>(request, error));
  }

  static async fetchAccountTransactions(
    request: FetchAccountTransactionsRequest
  ): Promise<FetchAccountTransactionsResponse> {
    const { id, page, page_size } = request;
    const { client, config } = this.createClient();

    return client
      .get(`${NexusRoutes.AccountTransactions(id)}?page_size=${page_size}&page=${page}`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountTransactionsResponse>(request, httpResponse);
        if (response.success) {
          response.nextPageUrl = httpResponse.data.next;
          response.transactions = httpResponse.data.results;
        }

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountTransactionsResponse>(request, error));
  }

  static async fetchAccountDocuments(request: FetchAccountDocumentsRequest): Promise<FetchAccountDocumentsResponse> {
    const { id, page_size, category } = request;
    const { client, config } = this.createClient();

    return client
      .get(`${NexusRoutes.AccountDocuments(id)}?category=${category}&page_size=${page_size}`, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchAccountDocumentsResponse>(request, httpResponse);
        if (response.success) {
          response.nextPageUrl = httpResponse.data.next;
          response.documents = httpResponse.data.results;
        }

        return response;
      })
      .catch(error => this.prepareResponse<FetchAccountDocumentsResponse>(request, error));
  }

  static async fetchBankAccount(request: FetchBankAccountRequest): Promise<FetchBankAccountResponse> {
    const { id } = request;
    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.BankAccount(id), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchBankAccountResponse>(request, httpResponse);
        if (response.success) response.bankAccount = httpResponse.data;
        return response;
      })
      .catch(error => {
        if (error.response && error.response.state === 404) {
          return this.prepareResponse<FetchBankAccountResponse>(request, error.response);
        }
        return this.prepareResponse<FetchBankAccountResponse>(request, error);
      });
  }

  static async updateBankAccount(request: UpdateBankAccountRequest): Promise<UpdateBankAccountResponse> {
    const { id } = request;
    const { client, config } = this.createClient();

    return client
      .put(NexusRoutes.UpdateBankAccount(id), request, config)
      .then(httpResponse => {
        const response = this.prepareResponse<UpdateBankAccountResponse>(request, httpResponse);

        return response;
      })
      .catch(error => {
        return this.prepareResponse<UpdateBankAccountResponse>(request, error);
      });
  }
}
